import React from "react";
import styles from "./in-page-legal.module.css";
import { Text } from "../../atoms/text/text";
import { safeHref } from "../../atoms/link/link";
import { InPageLegalFetch } from "./in-page-legal-fetch.client";
/* JSX only — a value from a "use client" module is a client reference here. */
import { Tooltip } from "../../atoms/tooltip/tooltip.client";
import { UNAVAILABLE_TEXT } from "../legal-modal/legal-content";
import { drawableColor, isInvisibleColor } from "../../services/css-color";
import { onceReporter, problemKey } from "../../services/report-once";
import { withSafeRender } from "../../atoms/safe-render";

/** Named as the Legal block set draws them — `Centered=False` / `Centered=True`. */
export type InPageLegalAlign = "left" | "center";

/** Production's `cta.position`: after the copy, or on a line of its own. */
export type InPageLegalCtaPosition = "inline" | "block";

/** What the link does: open its document in the Legal Modal, or go to its url. */
export type InPageLegalCtaAction = "modal" | "redirect";

export interface InPageLegalProps {
  /** The legal copy, plain or rich text with inline links. */
  legal_content?: string;
  /**
   * A legal document to show when `legal_content` is empty. Its plain
   * `legalLong_text` is fetched in the browser, as the production block does.
   */
  legal_url?: string;
  /** Wording of an optional link after the copy. Unset renders no link. */
  cta_label?: string;
  /**
   * Where the link goes. For `modal`, the document it opens — `legal_url` when
   * this is unset, unsafe or a fragment. For `redirect`, the page it goes to;
   * required, and an unsafe url renders no link.
   */
  cta_url?: string;
  /** `modal` (the default) opens the Legal Modal; `redirect` is a plain link. */
  cta_action?: InPageLegalCtaAction;
  /** `inline` (the default) follows the copy; `block` puts the link on its own line. */
  cta_position?: InPageLegalCtaPosition;
  /**
   * Explanation behind an info icon after the copy, as production's
   * `legalText.tooltip.content`. Unset renders no icon.
   */
  tooltip_content?: string;
  /** Accessible name of the info icon. Defaults to the Tooltip's own. */
  tooltip_label?: string;
  /** `left` (the default) or `center`. */
  text_align?: InPageLegalAlign;
  /**
   * Shown in place of the copy when `legal_url` cannot be loaded. Defaults to
   * production's wording.
   */
  unavailable_text?: string;
  /**
   * Colour of the copy and the See details link: a hex colour (`#rrggbb`),
   * `rgb()`/`rgba()`, `hsl()`/`hsla()` or a colour name. Any other format
   * (`oklch()`, `var(--…)`), `transparent` and a fully transparent colour are
   * ignored and the muted default stays. Contrast is the author's call.
   */
  text_color?: string;
  /**
   * Colour of every link — the copy's and See details. Same values as
   * `text_color`; unset, links take their text's colour. Always underlined.
   */
  link_color?: string;
  /**
   * The aside's id. Not authorable on the v3 canvas, which strips `id` before
   * spreading props — the canvas section carries the page anchor.
   */
  id?: string;
}

/* GTM's link position for the See details link, beside the Legal Modal's
   `modal_legal` and the snake_case positions the DDS blocks report. */
export const CTA_LINK_POSITION = "in_page_legal";

/* Own-property lookup, not a bare index — the CMS boundary is untyped and a
   bare index walks Object.prototype. Same guard FAQ carries for `layout`. */
const ALIGNS: Record<InPageLegalAlign, true> = {
  left: true,
  center: true
};

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/* The alpha rule now lives beside `drawableColor`; ZipCode applies it too. */
const invisible = isInvisibleColor;

/* Refused colours already reported, so a page drawn on every request warns
   once per mistake and not once per render — see services/report-once.ts. */
const reporter = onceReporter(message => {
  console.warn(message);
});

/** Forgets the colours already reported, so a suite reads its own warning
 *  rather than an earlier test's silence. Does nothing on a production build. */
export function forgetReportedColours(): void {
  reporter.forget();
}

/** The colour an author field may paint, or "" — warning once, outside a
 *  production build as Content Ribbon does, when a filled-in value is refused. */
function authorColour(field: string, value: unknown): string {
  const kept = drawableColor(value);
  const colour = kept && !invisible(kept) ? kept : "";
  const given = typeof value === "string" ? value.trim() !== "" : value !== undefined && value !== null;

  if (colour || !given || process.env.NODE_ENV === "production") {
    return colour;
  }
  reporter.report(
    problemKey(field, value),
    `[InPageLegal] ${field} ${JSON.stringify(typeof value === "string" ? value : typeof value)} is not ` +
      "a colour this block draws (a hex colour, rgb(), rgba(), hsl(), hsla() or a colour name, " +
      "and not transparent), so the default is used."
  );
  return colour;
}

/**
 * The small print under a section. Rebuilds production's DtvInPageLegal in
 * dark mode: the same copy, the same document fallback, no expand/collapse.
 *
 * Server Component. Client JavaScript ships only for the document fallback
 * (empty copy) and for a tooltip — `tooltip_content` or a `<tooltip>` in the
 * copy draws the client Tooltip atom.
 */
function InPageLegalImpl({
  legal_content: legalContent,
  legal_url: legalUrl,
  cta_label: ctaLabel,
  cta_url: ctaUrl,
  cta_action: ctaAction,
  cta_position: ctaPosition,
  tooltip_content: tooltipContent,
  tooltip_label: tooltipLabel,
  text_align: textAlign,
  unavailable_text: unavailableText,
  text_color: textColor,
  link_color: linkColor,
  id
}: InPageLegalProps) {
  const content = text(legalContent);
  const url = text(legalUrl);
  const label = text(ctaLabel);
  const align =
    typeof textAlign === "string" && Object.prototype.hasOwnProperty.call(ALIGNS, textAlign) ?
      textAlign :
      "left";
  /* Anything but the exact opt-in stays inline, as production's DtvText reads it. */
  const ctaBlock = ctaPosition === "block";
  const tip = text(tooltipContent);

  /* Nothing to say and nowhere to fetch it from renders nothing, as production
     does — an empty band of padding is not a legal block. */
  if (!content && !url) {
    return null;
  }

  /* Anything but the exact opt-in opens the modal, as production's CTA does. */
  const redirect = ctaAction === "redirect";
  /* safeHref returns "#" for a refused scheme. A redirect goes to cta_url
     alone (legal_url is a document, not a page) and may be a fragment; the
     modal takes the first of cta_url, legal_url that is not a fragment, since
     no fragment is a document. */
  const href = (redirect ? [text(ctaUrl)] : [text(ctaUrl), url])
    .filter(Boolean)
    .map(safeHref)
    .find(value => (redirect ? value !== "#" : !value?.startsWith?.("#")));
  /* Legal Modal's TRIGGER_ATTRIBUTE, spelled out: that module is "use client",
     so importing a value from it here yields a client reference, not the
     string. The spec pins the two together. */
  const opensModal = redirect ? {} : {
    "aria-haspopup": "dialog" as const,
    "data-legal-modal": ""
  };
  const cta = label && href ? (
    /* `block` makes this span the block — the link's box stays its words, and
       the line still follows text_align. */
    <Text as="span" variant="label" tone="muted" className={ctaBlock ? `${styles.cta} ${styles.ctaBlock}` : styles.cta}>
      <a
        href={href}
        {...opensModal}
        data-linkposition={CTA_LINK_POSITION}
        data-linkdestination={href}
      >
        {label}
      </a>
    </Text>
  ) : null;
  /* After the copy and before the link, as production's DtvText draws it. */
  const tooltip = tip ? (
    <Tooltip content={tip} label={text(tooltipLabel) || undefined} className={styles.tooltip} />
  ) : null;

  /* Author colours reach CSS as custom properties, and only once the shared
     validator has kept them as a colour — a CMS field is not a declaration —
     and they are not invisible. The text rule is opt-in by class: unset, the
     copy keeps its muted tone. React writes no undefined custom property. */
  const ownText = authorColour("text_color", textColor);
  const ownLink = authorColour("link_color", linkColor);
  const style = ownText || ownLink ? {
    "--ipl-text-color": ownText || undefined,
    "--ipl-link-color": ownLink || undefined
  } as React.CSSProperties : undefined;

  /* `dds-label` on the block itself, not only on its Text: the aside's own
     line box (its strut) is then the label's 12px / 1.3, so no line is
     taller than the copy on it — the designer's even-leading note. */
  const block = [styles?.legal, styles?.[align], "dds-label", ownText ? styles?.ownText : ""].filter(Boolean).join(" ");

  /* No copy: the fetcher draws the block once the document arrives — see its
     doc comment for production's three outcomes. */
  if (!content) {
    return (
      <InPageLegalFetch
        url={url}
        unavailableText={text(unavailableText) || UNAVAILABLE_TEXT}
        id={id}
        className={block}
        copyClassName={styles.copy}
        style={style}
      >
        {tooltip}
        {cta}
      </InPageLegalFetch>
    );
  }

  return (
    /* An <aside>, as production renders it: small print is tangential to the
       section it sits under. Not a <section>: the canvas already
       wraps every blok in one carrying the page anchor. */
    <aside className={block} id={id} style={style}>
      <Text as="p" variant="label" tone="muted" className={styles.copy}>
        {content}
      </Text>
      {tooltip}
      {cta}
    </aside>
  );
}
export const InPageLegal = withSafeRender(InPageLegalImpl, "InPageLegal");

export default InPageLegal;
